import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Copy, Gift, Users, Sparkles, Check, Music2, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/referrals")({
  component: ReferralsPage,
  head: () => ({
    meta: [
      { title: "Earn Free Months — Inquira Referrals" },
      {
        name: "description",
        content: "Share your Inquira referral link on TikTok and earn one free month of Premium for every qualifying paid subscriber you refer.",
      },
      { property: "og:title", content: "Earn free months of Inquira Premium" },
      { property: "og:description", content: "One qualified referral = one free month of Inquira Premium." },
      { property: "og:url", content: "https://www.inquira.online/referrals" },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/referrals" }],
  }),
});

interface Summary {
  referral_code: string | null;
  pro_bonus_until: string | null;
  total: number;
  pending: number;
  qualified: number;
  months_earned: number;
  months_redeemed: number;
  months_available: number;
  reversed: number;
}

function ReferralsPage() {
  const { user, isLoading, refreshPremium } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: rpcErr } = await supabase.rpc("my_referral_summary" as never);
    if (rpcErr) setError(rpcErr.message);
    else {
      setSummary(data as unknown as Summary);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    void load();
  }, [user, isLoading, navigate, load]);

  const shareUrl = summary?.referral_code
    ? `${typeof window !== "undefined" ? window.location.origin : "https://www.inquira.online"}/signup?ref=${summary.referral_code}`
    : "";

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      void track("referral_link_copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const handleShare = async () => {
    if (!shareUrl) return;
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          title: "Try Inquira",
          text: "Snap a photo of any homework question and get a clean, step-by-step explanation. Use my link:",
          url: shareUrl,
        });
        void track("referral_link_shared", { method: "web_share" });
        return;
      } catch { /* fall through */ }
    }
    void track("referral_link_shared", { method: "copy" });
    handleCopy();
  };

  const handleRedeem = async () => {
    setRedeeming(true);
    setNotice(null);
    const { data, error: rpcErr } = await supabase.rpc("redeem_referral_reward" as never);
    const res = data as { ok?: boolean; error?: string; months?: number } | null;
    if (rpcErr) setNotice(rpcErr.message);
    else if (res?.ok) {
      setNotice(`Applied ${res.months ?? 1} free month of Premium to your account.`);
      void track("referral_reward_redeemed", { months: res.months ?? 1 });
      await Promise.all([load(), refreshPremium()]);
    } else if (res?.error === "no_available_reward") {
      setNotice("You don't have an available free month to redeem yet.");
    } else {
      setNotice("We couldn't redeem that reward. Contact support@inquira.online.");
    }
    setRedeeming(false);
  };

  const bonusActive = summary?.pro_bonus_until && new Date(summary.pro_bonus_until) > new Date();

  return (
    <div className="min-h-screen notebook-lines px-4 pb-16">
      <div className="max-w-xl mx-auto pt-6 space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> Back to home
        </Link>

        <header className="text-center space-y-2">
          <div className="size-14 mx-auto rounded-full bg-neon-pink/10 flex items-center justify-center">
            <Gift className="size-7 text-neon-pink" />
          </div>
          <h1 className="font-display font-black text-3xl text-ink">Earn Free Months</h1>
          <p className="text-sm text-ink-muted">
            For every new customer who signs up with your link and becomes a qualifying paid Premium
            subscriber, you earn <strong>one free month</strong> of Inquira Premium.
          </p>
        </header>

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm rounded-xl p-3 border border-destructive/20">
            {error}
          </div>
        )}

        {loading || !summary ? (
          <div className="bg-card rounded-2xl p-6 border border-rule text-center text-sm text-ink-muted">
            Loading your referral link…
          </div>
        ) : (
          <>
            <section className="bg-card rounded-2xl p-6 border border-rule space-y-4">
              <div className="text-xs font-bold uppercase tracking-widest text-ink-muted">
                Your referral link
              </div>
              <div className="flex-1 bg-paper border-2 border-dashed border-neon-pink/40 rounded-xl py-4 text-center font-mono text-2xl font-black tracking-[0.35em] text-ink">
                {summary.referral_code ?? "—"}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="full" onClick={handleCopy} className="gap-2">
                  {copied ? <Check className="size-4 text-neon-green" /> : <Copy className="size-4" />}
                  {copied ? "Copied!" : "Copy link"}
                </Button>
                <Button variant="scan" size="full" onClick={handleShare} className="gap-2">
                  <Sparkles className="size-4" /> Share
                </Button>
              </div>
              {shareUrl && (
                <p className="text-[11px] text-ink-muted break-all text-center">{shareUrl}</p>
              )}
              <p className="text-[11px] text-ink-muted text-center">
                There's no need to create a new link for each person — share this one link everywhere.
              </p>
            </section>

            <section className="bg-card rounded-2xl p-6 border border-rule space-y-4">
              <div className="text-xs font-bold uppercase tracking-widest text-ink-muted flex items-center gap-2">
                <Users className="size-4" /> Your referrals
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <Stat label="Total" value={summary.total} />
                <Stat label="Pending" value={summary.pending} />
                <Stat label="Qualified" value={summary.qualified} />
                <Stat label="Months earned" value={summary.months_earned} />
                <Stat label="Redeemed" value={summary.months_redeemed} />
                <Stat label="Available" value={summary.months_available} />
              </div>

              {summary.months_available > 0 ? (
                <div className="rounded-xl border border-neon-green/40 bg-neon-green/10 p-4 space-y-3">
                  <div className="text-sm text-ink">
                    You have <strong>{summary.months_available}</strong> free{" "}
                    {summary.months_available === 1 ? "month" : "months"} ready to use.
                  </div>
                  <Button variant="scan" size="full" onClick={handleRedeem} disabled={redeeming} className="gap-2">
                    {redeeming ? <Loader2 className="size-4 animate-spin" /> : <Gift className="size-4" />}
                    {redeeming ? "Applying…" : "Redeem 1 free month"}
                  </Button>
                  <p className="text-[11px] text-ink-muted">
                    Redeeming adds Premium access time to your Inquira account. If your subscription is billed
                    by Apple or Google, it keeps renewing under that store's terms until you cancel there.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-ink-muted">
                  Free months appear here once a referred customer's paid subscription is verified.
                </p>
              )}

              {notice && <div className="text-xs text-ink bg-paper border border-rule rounded-xl p-3">{notice}</div>}
            </section>

            {bonusActive && (
              <section className="bg-neon-green/10 border border-neon-green/40 rounded-2xl p-6 text-center space-y-1">
                <div className="text-xs font-bold uppercase tracking-widest text-neon-green">
                  Free Premium time active
                </div>
                <div className="text-sm text-ink">
                  Premium through <strong>{new Date(summary.pro_bonus_until!).toLocaleDateString()}</strong>
                </div>
              </section>
            )}

            <section className="bg-card rounded-2xl p-6 border border-rule space-y-3 text-sm text-ink-muted">
              <h2 className="font-display font-bold text-ink text-base">How it works</h2>
              <ol className="space-y-2 list-decimal list-inside">
                <li>Share your Inquira referral link.</li>
                <li>Create an original TikTok about Inquira.</li>
                <li>A new customer signs up using your link.</li>
                <li>They become a qualifying paid Premium subscriber.</li>
                <li>You earn one free month.</li>
              </ol>
            </section>

            <section className="bg-card rounded-2xl p-6 border border-rule space-y-3">
              <h2 className="font-display font-bold text-ink text-base flex items-center gap-2">
                <Music2 className="size-4 text-neon-blue" /> Create your TikTok
              </h2>
              <p className="text-sm text-ink-muted">
                Make an original TikTok showing or discussing your experience with Inquira, and point viewers
                to your unique referral link.
              </p>
              <p className="text-xs text-ink-muted">
                If you receive rewards for promoting Inquira, clearly disclose your relationship with Inquira in
                accordance with applicable advertising and platform rules. TikTok does not sponsor, endorse,
                administer or participate in this program.
              </p>
            </section>

            <section className="bg-card rounded-2xl p-6 border border-rule space-y-2 text-xs text-ink-muted">
              <h2 className="font-display font-bold text-ink text-sm">Important referral terms</h2>
              <p>
                A reward is not earned merely because someone clicks your link or starts a free trial. The
                referred customer must meet Inquira's eligibility requirements and become a verified qualifying
                paid subscriber. Self-referrals, duplicate or fraudulent accounts, payment reversals,
                chargebacks and refunded subscriptions do not qualify.
              </p>
              <Link to="/referral-terms" className="underline text-ink">
                Read the full Referral Program Terms
              </Link>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-paper rounded-xl border border-rule py-3">
      <div className="text-lg font-black text-ink">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
    </div>
  );
}
